import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import Grid, { gridClasses } from "@mui/material/Grid";
import _ from "lodash";
import FormElement from "../FormElement";
import { scrollToAnchor } from "../../../../theme/commonStyles";
import { SubFormDefinition } from "../../../../types/FormDefinitions/FullFormDefinition";
import { ProductUnion } from "../../../../types/ProductUnion";
import { UseForm } from "../../../../types/UseForm";
import { getRequiredFields } from "../../../../services/StatusChangeValidators/statusChangeValidators";
import { ExpandMore } from "@mui/icons-material";

type SubFormSectionProps = {
  getAnchorId: Function;
  formSection: SubFormDefinition["sections"][number];
  product: ProductUnion;
  formDisabled: boolean;
  useFormProps: UseForm;
  refreshPage: Function;
  asAccordion?: boolean;
};

export default function SubFormSection({
  getAnchorId,
  formSection,
  asAccordion = false,
  ...rest
}: SubFormSectionProps) {
  return (
    <>
      <Box sx={scrollToAnchor} id={getAnchorId(formSection.label)} />
      {asAccordion && (
        <Accordion elevation={2}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography component={"span"}>{formSection.label}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Content getAnchorId={getAnchorId} formSection={formSection} {...rest} />
          </AccordionDetails>
        </Accordion>
      )}
      {!asAccordion && (
        <>
          <Box
            sx={{
              textAlign: "center",
              marginTop: "2rem",
            }}
          >
            <Typography variant="h3">{formSection.label}</Typography>
          </Box>
          <Content getAnchorId={getAnchorId} formSection={formSection} {...rest} />
        </>
      )}
    </>
  );
}

const Content = ({ formSection, useFormProps, product, formDisabled, refreshPage }: SubFormSectionProps) => {
  const mandatoryFields = getRequiredFields(product);
  return (
    <Grid
      container
      sx={(theme) => ({
        [`& > .${gridClasses.root}`]: {
          [theme.breakpoints.up("md")]: {
            padding: "12px",
          },
          minWidth: "320px",
        },
      })}
    >
      {_.map(formSection.elements, (formElementProps) => {
        if (
          formElementProps.isHidden &&
          formElementProps.isHidden({
            values: useFormProps.values,
            product,
          })
        ) {
          return null;
        }

        //important! overwrites all individual isDisabled configurations
        if (formDisabled) {
          formElementProps.isDisabled = () => true;
        }

        const fullWidth = formElementProps.additionalProps && formElementProps.additionalProps.fullWidth;

        return (
          <Grid
            key={formElementProps.path}
            size={{
              xs: 12,
              md: !fullWidth && 6,
              lg: !fullWidth && 4,
            }}
          >
            <FormElement
              product={product}
              {...formElementProps}
              {...useFormProps}
              mandatoryFields={mandatoryFields}
              refreshPage={refreshPage}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};
